import {useRepetitiveTasks, useRequiredConfig} from '@domain/shifthandover'
import {useCancelInactiveQueries} from '@hconnect/common/hooks/useCancelInactiveQueries'
import {combineSearchParamsToQueryString} from '@hconnect/common/query-params'
import {ShiftEvent} from '@hconnect/common/types'
import type {PaginationOptions} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {useQueryClient} from '@tanstack/react-query'
import {
  filterSettingsToUrlParams,
  FilterOptions,
  getFilterDifference,
  Page
} from '@widgets/shift-event-filters'
import {useState, useEffect} from 'react'
import {generatePath, useNavigate, useParams} from 'react-router'

import {RecurringTasksView} from '../containers/recurringTasks'
import {useFilterDatePicker} from '../hooks/useDatePicker'
import {usePageFilters} from '../hooks/useEventsFilter'
import {
  modeAfterClose,
  useQueryParameterForViewMode,
  viewModeToUrlParams
} from '../hooks/useQueryParameter'
import {useTrackAnalyticsEvent} from '../hooks/useTrackAnalyticsEvents'
import {routeToRecurringTasks} from '../routes'
import {ViewMode} from '../types'

const createUrl = (
  nextFilterOptions: FilterOptions,
  nextViewMode: ViewMode,
  plantId: string
): string => {
  const filterParams = filterSettingsToUrlParams(nextFilterOptions)
  const viewModeParams = viewModeToUrlParams(nextViewMode)
  const queryString = combineSearchParamsToQueryString(filterParams, viewModeParams)
  return `${generatePath(routeToRecurringTasks, {plantId})}?${queryString}`
}

export const RecurringTasksPage = () => {
  const navigate = useNavigate()
  const {plantId} = useParams<{plantId: string}>()
  if (!plantId) throw new Error('plantId is required')
  const {country, shifts} = useRequiredConfig(plantId)
  const viewMode = useQueryParameterForViewMode(plantId)
  const trackAnalyticsEvent = useTrackAnalyticsEvent({plantId, country})
  const [pageNumber, changePage] = useState<number>(0)
  const [itemsPerPage, setItemsPerPage] = useState<number>(50)
  const [selectedSchedule, setSelectedSchedule] = useState<ShiftEvent | undefined>()
  const timezone = useUpmPlantTimezone(plantId)
  const [filterOptions, setFilters] = usePageFilters(plantId, Page.RecurringTasks)
  const {getDateRange} = useFilterDatePicker(plantId, shifts)
  const extendedFilterOptions = {...filterOptions}
  if (!extendedFilterOptions.timeRange) {
    extendedFilterOptions.timeRange = getDateRange()
  }

  const queryClient = useQueryClient()
  useCancelInactiveQueries(queryClient)

  const {data, isLoading, isSuccess} = useRepetitiveTasks({
    plantId,
    timezone,
    filters: filterOptions,
    itemsPerPage,
    pageNumber
  })

  useEffect(() => {
    if (
      isSuccess &&
      data &&
      viewMode?.mode !== 'createMode' &&
      viewMode?.mode !== 'tableOnlyMode' &&
      viewMode?.itemId
    ) {
      const selectedItem = data.taskSchedules.find(
        (item) => (item as ShiftEvent).id === viewMode.itemId
      )
      setSelectedSchedule(selectedItem as ShiftEvent)
    }
  }, [isSuccess, data, viewMode])

  const openDetailsMode = (schedule: ShiftEvent) => {
    void navigate(createUrl(filterOptions, {mode: 'detailsMode', itemId: schedule.id}, plantId))
    setSelectedSchedule(schedule)
  }

  const doClose = (original?: ShiftEvent, editVersion?: ShiftEvent) => {
    const nextMode: ViewMode = modeAfterClose(original, editVersion)
    void navigate(createUrl(filterOptions, nextMode, plantId))

    if (nextMode.mode === 'tableOnlyMode') {
      setSelectedSchedule(undefined)
    }
  }

  const onPaginationChange = (pageNumber: number, itemsPerPage: number) => {
    changePage(pageNumber)
    setItemsPerPage(itemsPerPage)
  }

  const paginationOptions: PaginationOptions = {
    onPaginationChange,
    page: pageNumber,
    rowsPerPage: itemsPerPage,
    totalDataLength: data?.totalItemCount ?? 0
  }

  return (
    <RecurringTasksView
      plantId={plantId}
      activeFilter={filterOptions}
      data={data}
      isLoading={isLoading}
      viewMode={viewMode}
      openDetailsMode={openDetailsMode}
      onSideCardClose={doClose}
      setViewMode={(nextViewMode) => {
        void navigate(createUrl(filterOptions, nextViewMode, plantId))
      }}
      onFilterChange={(nextFilterOptions) => {
        changePage(0)
        setFilters(nextFilterOptions)
        trackAnalyticsEvent('userAdjustsFilter', {
          filterPage: Page.RecurringTasks,
          filterName: getFilterDifference(nextFilterOptions, filterOptions)
        })
      }}
      paginationOptions={paginationOptions}
      selectedSchedule={selectedSchedule}
    />
  )
}
